import type { IsAny, IsUnknown, Primitive } from "ts-essentials";

import { IS_DEV, IS_NODE, IS_NODE_BROWSERLESS } from "@/__main__/constants.mjs";
import mainRefs from "@/__main__/refs.mjs";
import { devWarn } from "@/util/dev.mjs";
import isCyclic from "@/util/is-cyclic.mjs";
import isEmpty from "@/util/is-empty.mjs";
import symbolName, { symbolMap, symbolPrefix } from "@/util/symbol-name.mjs";

/** Used internally to locate the root model. */
const rootModel: unique symbol = symbolName("root-model");
const currentData: unique symbol = symbolName("current-data");
const originalFunc: unique symbol = symbolName("original-func");

// Indicates that the field has been validated
const isValidated: unique symbol = symbolName("is-validated");

export {
  isMap as __INTERNAL_IS_MAP_SYMBOL,
  rootModel as __INTERNAL_ROOT_MODEL_SYMBOL,
  isValidated as __INTERNAL_VALIDATION_SYMBOL,
};

// Map the data to the model
export const dataModelKey: unique symbol = symbolName("data-model");

/**
 * Indicate where in the model is considered the "root" element. Used to determine
 * the root model when a free-standing transform function is used without a post-transform
 * validator.
 *
 * Generally this should be discouraged versus using the `.transform()` function, since it
 * can lead to unexpected behavior when models are nested.
 *
 * @example
 * const apiModelValidation = createModel({
 *   data: {
 *     summoner: {
 *       id: Number,
 *       name: String,
 *       [isRootModel]: true,
 *     }
 *   }
 * });
 *
 * // Necessary here, since otherwise the returned data would be validated against `model` instead
 * // of `model.data.summoner`.
 * function transform(data) {
 *     return apiModelValidation(data).data.summoner;
 * }
 *
 * @example
 * const model = {
 *   id: Number,
 *   name: String,
 * } satisfies ModelDefintion;
 *
 * // Not necessary here, since the root is defined by the first argument to `.transform()` below.
 * const SummonerModel = createModel({
 *   data: {
 *     summoner: model
 *   }
 * }).transform(model, (data) => data.data.summoner);
 */
export const isRootModel: unique symbol = symbolName("is-root-model");

type RootModel<T extends ModelDefinition = ModelDefinition> = T & {
  [isRootModel]: true;
};

const isAny: unique symbol = symbolName("is-any");
const isUnknown: unique symbol = symbolName("is-unknown");

type Any = {
  [isAny]: true;
};

type Unknown = {
  [isUnknown]: true;
};

/**
 * Allow any type to exist in a model.
 */
export const Any: Any = Object.freeze({
  [isAny]: true,
});

/**
 * Allow any type to exist in a model, but instead has the TS type of `unknown`, requiring
 * explicit checks and casting to use.
 */
export const Unknown: Unknown = Object.freeze({
  [isUnknown]: true,
});

/**
 * Allow arbitrary keys to exist in the object.
 *
 * @deprecated Prefer using {@link Mapped} instead.
 */
export const arbitraryKeys: unique symbol = symbolName("arbitrary-keys");

const isSilent: unique symbol = symbolName("is-silent");

type IsSilent<T extends ModelDefinition = ModelDefinition> = T & {
  [isSilent]: true;
};

/**
 * Mark a field as silent. This will cause properties that are missing to be initialized, and
 * values of differing types to be cast to the expected type.
 *
 * This matches exactly with the default behavior except that it will silence warnings on
 * any type mismatch. This is particularly useful if certain fields are expected to be missing
 * from the data source.
 */
export function Silent<T extends ModelDefinition>(arg: T): IsSilent<T> {
  return applyFlag(arg, isSilent);
}

const isOptional: unique symbol = symbolName("is-optional");

type IsOptional<T extends ModelDefinition = ModelDefinition> = T & {
  [isOptional]: true;
};

/**
 * Mark a field as optional.
 *
 * Note: if the field is `null`, the key will be deleted instead of remaining `null`, however this can
 * be overridden by using a `Union` with a `null` entry.
 */
export function Optional<T extends ModelDefinition>(arg: T): IsOptional<T> {
  return applyFlag(arg, isOptional);
}

const isPartial: unique symbol = symbolName("is-partial");

type IsPartial<T extends ModelObject = ModelObject> = T & {
  [isPartial]: true;
};

/**
 * Mark an object as Partial. This will allow properties on it to not exist, but will enforce their
 * values if the key exists.
 */
export function Partial<T extends ModelObject>(arg: T): IsPartial<T> {
  return applyFlag(arg, isPartial);
}

const isDeepPartial: unique symbol = symbolName("is-deep-partial");

type IsDeepPartial<T extends ModelObject = ModelObject> = T & {
  [isDeepPartial]: true;
};

/**
 * Mark all decendent objects as partial. See {@link Partial} for more details.
 */
export function DeepPartial<T extends ModelObject>(arg: T): IsDeepPartial<T> {
  return applyFlag(arg, isDeepPartial);
}

const isUnion: unique symbol = symbolName("is-union");

export type IsUnion<T extends readonly ModelValue[] = readonly ModelValue[]> =
  T & {
    [isUnion]: true;
  };

/**
 * Mark a field as allowing one of a set of values. These could be exact primitives,
 * or other models, however only the first matched, valid model is used.
 *
 * If no model is matched, the final one will be used.
 */
export function Union<const T extends readonly ModelValue[]>(
  values: T,
): IsUnion<T> {
  return applyFlag(values, isUnion);
}

export function isUnionModel<const T extends readonly (ModelValue | object)[]>(
  value: T,
): value is T & { [isUnion]: true } {
  return hasSymbol(isUnion, value);
}

const isTuple: unique symbol = symbolName("is-tuple");

type IsTuple<T extends readonly ModelValue[] = readonly ModelValue[]> = T & {
  [isTuple]: true;
};

/**
 * Mark a field as as being a fixed length array of values.
 */
export function Tuple<const T extends readonly ModelValue[]>(
  values: T,
): IsTuple<T> {
  return applyFlag(values, isTuple);
}

export function isTupleModel<const T extends readonly (ModelValue | object)[]>(
  value: T,
): value is T & { [isTuple]: true } {
  return hasSymbol(isTuple, value);
}

const isMap: unique symbol = symbolName("is-map");

type IsMapKey =
  | Any
  | Unknown
  | StringConstructor
  | NumberConstructor
  | SymbolConstructor
  | ((value?: unknown) => PropertyKey)
  | IsUnion<readonly ModelValue[]>;

type IsMap<
  K extends IsMapKey = IsMapKey,
  V extends ModelDefinition = ModelDefinition,
> = {
  [isMap]: { key: K; value: V };
};

/**
 * Mark a field as being a mapping of arbitrary keys to values.
 */
export function Mapped<K extends IsMapKey, V extends ModelDefinition>(
  arg: Readonly<{ key: K; value: V }>,
): IsMap<K, V> {
  return { [isMap]: arg };
}

export function isMappedModel<K extends IsMapKey>(
  arg: ModelDefinition,
): arg is IsMap<K, ModelDefinition> {
  return arg[isMap] !== undefined;
}

function applyFlag(arg: object | ((..._: unknown[]) => unknown), flag: symbol) {
  if (typeof arg === "function") {
    const fn = arg.bind(null); // This works similar to typecasting functions.

    fn[flag] = true;

    // Keep the original function around, so we can identify primitive constructors.
    // Otherwise equality checks for them will not pass.
    fn[originalFunc] = arg;

    return fn;
  } else if (typeof arg === "object") {
    return Object.assign(Array.isArray(arg) ? [] : {}, arg, { [flag]: true });
  }

  throw new TypeError(`Invalid parameter: "${arg}"`);
}

export type FromModel<T extends object> =
  T extends TransformModelFn<unknown, infer V> ? V : ResolveModel<T>;

export type TransformModelFn<I, O> = ((
  data: I,
  config?: ValidationConfig,
) => O) & {
  [rootModel]?: ModelDefinition;

  skipDeepEqual?: boolean;
};

type ResolveModelState = object & {
  [isDeepPartial]?: boolean;
};

type ResolveModel<T, State extends ResolveModelState = object> = T extends Any
  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  : IsAny<T> extends true
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any
    : T extends Unknown
      ? unknown
      : IsUnknown<T> extends true
        ? unknown
        : IsNever<T> extends true
          ? never
          : T extends IsSilent<infer O>
            ? ResolveModel<O, State>
            : T extends IsDeepPartial<infer O>
              ? ResolveModel<O, State & { [isDeepPartial]: true }>
              : T extends IsPartial<infer O>
                ? Partial<ResolveModel<O, State>>
                : T extends IsUnion<infer A>
                  ? A extends ReadonlyArray<infer E>
                    ? // Unions cannot resolve to `undefined`, so we can safely exclude `undefined` from the result
                      Exclude<ResolveModel<E, State>, undefined>
                    : never
                  : T extends IsTuple<infer V>
                    ? ResolveTuple<V, State>
                    : // Check for common constructors
                      T extends StringConstructor
                      ? string
                      : T extends NumberConstructor
                        ? number
                        : T extends BooleanConstructor
                          ? boolean
                          : T extends Constructor<infer V>
                            ? V
                            : T extends TransformModelFn<unknown, infer V>
                              ? V
                              : // Array handling
                                T extends ReadonlyArray<infer V>
                                ? ResolveModel<V, State>[]
                                : // Pull the return value from functions
                                  T extends (...args: unknown[]) => unknown
                                  ? T extends (value: unknown) => infer R
                                    ? R
                                    : never
                                  : T extends object
                                    ? ResolveModelObject<
                                        // Pick out the optional fields, mark them optional
                                        Partial<Pick<T, PickOptionalKeys<T>>> &
                                          // Omit the optional fields, the `isOptional` symbol, and resolve the values
                                          Omit<
                                            T,
                                            | PickOptionalKeys<T>
                                            | typeof isOptional
                                          >,
                                        State
                                      >
                                    : T;

type PickOptionalKeys<T extends object> = {
  [K in keyof T]: T[K] extends IsOptional ? K : never;
}[keyof T];

type ResolveModelObject<
  O extends object,
  State extends ResolveModelState,
> = O extends infer T
  ? T extends RootModel
    ? Omit<T, typeof isRootModel> extends infer O extends object
      ? ResolveModelObject<O, State>
      : never
    : T extends {
          [arbitraryKeys]: infer V;
        }
      ? Omit<T, typeof arbitraryKeys> extends infer O extends object
        ? ResolveModelObject<O, State> &
            ResolveModel<
              {
                [key: string]: V;
              },
              State
            >
        : never
      : T extends IsMap<infer K, infer V>
        ? Omit<T, typeof isMap> extends infer O extends object
          ? (O extends Record<string, never>
              ? unknown
              : ResolveModelObject<O, State>) &
              Partial<
                ResolveModel<
                  K extends Any | Unknown
                    ? Record<PropertyKey, V>
                    : K extends StringConstructor
                      ? Record<string, V>
                      : K extends NumberConstructor
                        ? Record<number, V>
                        : K extends SymbolConstructor
                          ? Record<symbol, V>
                          : K extends (value?: unknown) => infer K
                            ? K extends PropertyKey
                              ? Record<K, V>
                              : never
                            : K extends IsUnion<ReadonlyArray<PropertyKey>>
                              ? { [P in K[number]]: V }
                              : never,
                  State
                >
              >
          : never
        : {
              [K in keyof T as K extends `${string}:${infer End}`
                ? End
                : K]: ResolveModel<T[K], State>;
            } extends infer O
          ? State extends IsDeepPartial
            ? Partial<O>
            : O
          : never
  : never;

type ResolveTuple<T, State extends ResolveModelState> =
  T extends Readonly<[infer Head, ...infer Tail]>
    ? readonly [ResolveModel<Head, State>, ...ResolveTuple<Tail, State>]
    : T;

type TypeFunction = (value?: unknown) => unknown;

export type ModelValue = ModelDefinition | Primitive;

export type ModelObject = {
  [key: string]: ModelValue;

  [isRootModel]?: boolean;
  [isSilent]?: true;
  [isOptional]?: true;
  [isPartial]?: true;
} & (
  | {
      [isMap]: {
        key: IsMapKey;
        value: ModelValue;
      };

      [arbitraryKeys]?: never;
    }
  | { [isMap]?: never; [arbitraryKeys]: ModelValue }
  | { [isMap]?: never; [arbitraryKeys]?: never }
);

export type ModelDefinition =
  | Any
  | Unknown
  | StringConstructor
  | NumberConstructor
  | BooleanConstructor
  | Uint8ArrayConstructor
  | Uint16ArrayConstructor
  | TypeFunction
  // We cannot check for `IsUnion` or `IsTuple` here it would result
  // in potentially infinite recursion.
  | ((ModelValue[] | readonly ModelValue[]) & {
      [isUnion]: true;
    })
  | ((ModelValue[] | readonly ModelValue[]) & {
      [isTuple]: true;
    })
  | (readonly [ModelValue] & {
      [isRootModel]?: true;
      [isSilent]?: true;
      [isOptional]?: true;
    })
  | ModelObject;

export type ModelValidateFn<M extends ModelDefinition> =
  InternalModelValidateFn<ResolveModel<M>>;

type InternalModelValidateFn<ResolvedModel> = TransformModelFn<
  unknown,
  ResolvedModel
> & {
  transform: <const M extends ModelDefinition>(
    transformedModel: M,
    fn: (data: ResolvedModel) => ResolveModel<M> extends infer O ? O : never,
  ) => TransformModelFn<ResolvedModel, ReturnType<typeof fn>>;
};

const MAX_WARNINGS = 16;

export default function createModel<M extends ModelDefinition>(
  model: M,
): ModelValidateFn<M> {
  const cycleTrace = isCyclic(model);
  if (cycleTrace) {
    throw new Error(`Cycle detected in model: ${cycleTrace}-> <CYCLE>`);
  }

  const root = findRootModel(model);

  const onValidateModel = function onValidateModel(
    data: unknown,
    config?: ValidationConfig,
  ) {
    if (data && typeof data === "object" && data[rootModel]) {
      throw { [rootModel]: root };
    }

    return validateModel(data, { root, model, config }) as ResolveModel<M>;
  };

  onValidateModel[rootModel] = root;

  onValidateModel.transform = <Result extends ModelDefinition>(
    transformedModel: Result,
    fn: (data: ResolveModel<M>) => ResolveModel<Result>,
  ) => {
    if (IS_DEV) {
      // If the `findRootModel` above found a root that wasn't the model itself
      if (root !== model) {
        devWarn(
          "Defining an `[isRootModel]` when you're utilizing .transform() is not necessary.",
          {
            model,
            root,
            transformedModel,
          },
        );
      }

      const transformedRoot = findRootModel(transformedModel);

      if (transformedRoot !== transformedModel) {
        devWarn(
          "Defining an `[isRootModel]` in a call to `.transform()` is not necessary, as the first argument is the root model.",
          {
            model,
            root,
            transformedModel,
            transformedRoot,
          },
        );
      }
    }

    function onValidateAndTransformModel(
      data: unknown,
      config?: ValidationConfig,
    ): ResolveModel<Result> {
      data = fn(onValidateModel(data, config));

      return validateModel(data, {
        root: transformedModel,
        model: transformedModel,
        config,
      }) as ResolveModel<Result>;
    }

    onValidateAndTransformModel[rootModel] = transformedModel;

    return onValidateAndTransformModel;
  };

  return onValidateModel as ModelValidateFn<M>;
}

function validateModel(
  data: unknown,
  {
    root,
    model,
    config,
  }: {
    root: ModelDefinition;
    model: ModelDefinition;
    config?: ValidationConfig;
  },
): unknown {
  const state = {
    key: "<root>",
    path: [],

    failOnError: config?.failOnError ?? false,

    [rootModel]: root,
    [currentData]: data,

    silent: false,
    warnings: {
      count: 0,
    },

    config,
  };

  const result = validate(model, data, state);

  if (state.warnings.count > MAX_WARNINGS) {
    devWarn(`Validation logged *${state.warnings.count}* warnings.`);
  }

  return result;
}

export type ValidationConfig = {
  isPostValidation?: boolean;
  removeEmpty?: boolean;
  failOnError?: boolean;
};

type ValidationState = {
  key: string;
  path: (string | number)[];

  /** If any children fail validation for any reason (silents should not be cast),
   * the parent will fail validation as well. */
  failOnError: boolean;

  /** If all child objects should be marked as {@link isPartial} */
  [isDeepPartial]?: boolean;

  /** If the current value fails validation, it should be cast to the proper type. */
  [isSilent]?: boolean;

  [rootModel]: ModelDefinition;
  [currentData]: unknown;

  silent: boolean;
  warnings: { count: number };
  mismatchCount?: number;

  config?: ValidationConfig;
};

function logValidationWarning(
  state: ValidationState,
  message: string,
  params: object = {},
) {
  if (state.silent) return;

  if (state.warnings.count < MAX_WARNINGS) {
    devWarn(message, {
      ...params,

      path: state.path.join("."),
      rootModel: state[rootModel],
      input: state[currentData],
    });
  }

  state.warnings.count++;
}

function stringifyDefinition(definition: ModelValue) {
  if (definition === null) return "null";

  if (isType<Any>(isAny, definition)) {
    return "anything";
  }

  if (isType<Unknown>(isUnknown, definition)) {
    return "unknown";
  }

  if (isType<IsUnion>(isUnion, definition)) {
    return `one of [${definition.map(stringifyDefinition).join(", ")}]`;
  }

  if (isType<IsTuple>(isTuple, definition) || Array.isArray(definition)) {
    return !definition[isTuple] ? "array" : "tuple";
  }

  if (typeof definition === "object") {
    return "object";
  }

  if (typeof definition === "function") {
    // Primitive constructors to their equivalent `typeof` names
    if (
      definition === String ||
      definition === Number ||
      definition === Boolean
    ) {
      return typeof definition();
    }

    return definition.name;
  }

  // Literal types
  if (typeof definition === "string") {
    return `"${definition}"`;
  }

  return `${String(definition)}`;
}

const builtInPrimitives = new Set<string>([
  "String",
  "Number",
  "Boolean",
  // Excluding symbol here because we need to be casting to symbol.
]);
function isDefinitionCustom(definition: ModelValue): boolean {
  if (typeof definition !== "function") return false;
  // Have to duck-type this because definition may be a cloned function.
  if (builtInPrimitives.has(definition.name)) return false;
  return true;
}
function hasCasts(definition: ModelValue): boolean {
  for (const key in definition as object) {
    const v = definition[key];
    if (typeof v === "object" && hasCasts(v)) return true;
    if (typeof v !== "function") continue;
    if (!builtInPrimitives.has(v.name)) return true;
  }
  return false;
}

function mismatchedTypeWarning(
  state: ValidationState,
  definition: ModelValue,
  value: unknown,
) {
  if (state.silent || isDefinitionCustom(definition)) return;

  let type: string;

  if (value === undefined) type = "undefined";
  else if (value === null) type = "null";
  else if (typeof value === "function") type = value.name;
  else if (typeof value === "symbol") type = value.description;
  else if (typeof value === "object" && Array.isArray(value)) type = `array`;
  else type = typeof value;

  logValidationWarning(
    state,
    `Expected ${stringifyDefinition(definition)} at "${
      state.key
    }", got ${type}.`,
    { definition, value: value },
  );
}

function hasSymbol<S extends symbol, T extends object | Primitive>(
  s: S,
  o: T,
): o is T & { [K in S]: true } {
  if (typeof o === "object" || typeof o === "function") {
    // @ts-expect-error
    return o[s] === true;
  }

  return false;
}

function isType<T extends ModelValue>(s: symbol, o: ModelValue): o is T {
  return hasSymbol(s, o);
}

function validate<M>(
  dataDefinition: ModelValue,
  data: unknown,
  state: ValidationState,
): M;

function validate<M>(
  dataDefinition: ModelValue,
  data: ReadonlyArray<unknown>,
  state: ValidationState,
): M[];

function validate<M>(
  dataDefinition: ModelValue,
  data: unknown | ReadonlyArray<unknown>,
  state: ValidationState,
): M | M[] {
  // If they pass in a definition that was created through `createModel` already, then
  // yank out the root model from it
  if (typeof dataDefinition === "function") {
    if (dataDefinition[rootModel]) {
      dataDefinition = dataDefinition[rootModel];
    }
  }

  // Pull in state from the definition
  state[isDeepPartial] =
    state[isDeepPartial] || dataDefinition?.[isDeepPartial];

  state[isSilent] = state[isSilent] || dataDefinition?.[isSilent];

  // Handle exact matches
  if (
    dataDefinition === null ||
    (typeof dataDefinition !== "object" && typeof dataDefinition !== "function")
  ) {
    // Resolve exact symbols
    if (typeof dataDefinition === "symbol" && typeof data === "string") {
      const foundSymbol = resolveSymbol(data);

      if (foundSymbol) {
        data = foundSymbol;
      }
    }

    if (data === dataDefinition) {
      return data as unknown as M;
    }

    if (state.failOnError) {
      return undefined;
    }

    state.mismatchCount++;
    if (!state[isSilent]) {
      mismatchedTypeWarning(state, dataDefinition, data);
    }

    // Fall back to the exact value
    return dataDefinition as unknown as M;
  }

  if (
    isType<Any>(isAny, dataDefinition) ||
    isType<Unknown>(isUnknown, dataDefinition)
  ) {
    if (typeof data === "object" && data !== null) {
      data[isValidated] = true;
      return data as unknown as M;
    }

    return data as M;
  }

  if (isType<IsUnion>(isUnion, dataDefinition)) {
    if (dataDefinition.length === 0) {
      logValidationWarning(
        state,
        `Data model union at ${state.key} must have at least one element.`,
      );

      return undefined;
    }

    for (const itemDefinition of dataDefinition) {
      const value = validate<M>(itemDefinition, data, {
        ...state,

        failOnError: true,

        silent: true,
      });

      if (value !== undefined) {
        return value;
      }
    }

    if (state.failOnError) {
      return undefined;
    }

    state.mismatchCount++;
    if (!state[isSilent]) {
      mismatchedTypeWarning(state, dataDefinition, data);
    }

    // Unions that are not valid fall back to the last definition
    return validate(dataDefinition[dataDefinition.length - 1], undefined, {
      ...state,

      silent: true,
    });
  }

  if (typeof dataDefinition === "function") {
    return matchType(data, dataDefinition, state) as M;
  }

  if (
    Array.isArray(dataDefinition) ||
    isType<IsTuple>(isTuple, dataDefinition)
  ) {
    if (!dataDefinition[isTuple] && dataDefinition.length !== 1) {
      logValidationWarning(
        state,
        `Data model array at ${state.key} must have exactly one element.`,
      );
    }

    const arr: M[] = [];

    // Quick fix to copy symbols over from data.
    for (const symbol of Object.getOwnPropertySymbols(data || {})) {
      arr[symbol] = data[symbol];
    }

    if (dataDefinition.length) {
      if (!Array.isArray(data)) {
        if (state.failOnError) {
          return undefined;
        }

        state.mismatchCount++;
        if (!state[isSilent] && data !== undefined && data !== null) {
          mismatchedTypeWarning(state, dataDefinition, data);
        }

        data = [];

        // Ensure the we only warn about the current value, not all of the
        // nested values which will also be undefined
        state.silent = true;
      }

      // If the array is a tuple, it's a fixed length, otherwise the length is the data length
      const len = isType<IsTuple>(isTuple, dataDefinition)
        ? dataDefinition.length
        : (data as unknown[]).length;

      // UNSAFE SSR OPTIMIZATION:
      // skip validating the entire length of an array, if the first has no mismatch
      const skipValidate =
        IS_NODE_BROWSERLESS &&
        !isType<IsTuple>(isTuple, dataDefinition) &&
        !hasCasts(dataDefinition);

      const prevCount = state.mismatchCount;
      for (let i = 0; i < len; i++) {
        if (i > 0 && skipValidate && state.mismatchCount === prevCount) {
          arr.push(data[i]);
          continue;
        }

        // Use the tuple definition by index, or fall back to index zero for normal arrays.
        const itemDefinition: (typeof dataDefinition)[number] =
          dataDefinition[dataDefinition[isTuple] ? i : 0];

        const value = validate<M>(itemDefinition, data[i], {
          key: `${state.key}.${i}`,
          path: [...state.path, i],

          failOnError: state.failOnError,

          [isDeepPartial]: state[isDeepPartial],

          [rootModel]: state[rootModel],
          [currentData]: state[currentData],

          silent: state.silent,
          warnings: state.warnings,

          config: state.config,
        });

        if (value === undefined) {
          if (state.failOnError) {
            return undefined;
          }
        }

        arr.push(value);
      }
    }

    arr[isValidated] = true;

    return arr;
  }

  if (typeof data !== "object" || Array.isArray(data) || !data) {
    if (state.failOnError) {
      return undefined;
    }

    state.mismatchCount++;
    if (!state[isSilent] && data !== undefined && data !== null) {
      mismatchedTypeWarning(state, dataDefinition, data);
    }

    data = {};

    // Ensure the we only warn about the current value, not all of the
    // nested values which will also be undefined
    state.silent = true;
  }

  // If we want to fail on errors, we need to construct a new object so we don't mutate the original data
  if (state.failOnError) {
    data = { ...(data as object) };
  }

  // Get all of the keys we need to validate
  const allKeys = new Set<string>([
    ...Object.keys(dataDefinition),
    ...Object.keys(data),
  ]);

  const aliasedKeys = Object.keys(dataDefinition).reduce((set, k) => {
    const keySplit = k.split(":");
    if (keySplit[1])
      set.add(state.config?.isPostValidation ? keySplit[1] : keySplit[0]);
    return set;
  }, new Set<string>([]));

  const keyDefinition = dataDefinition[isMap]?.key ?? Any;

  const isObjectPartial = !!(
    state[isDeepPartial] || dataDefinition?.[isPartial]
  );

  for (const fullKey of allKeys) {
    const keySplit = dataDefinition[arbitraryKeys]
      ? [fullKey]
      : fullKey.split(":");
    let key = keySplit[0];
    const alias = keySplit[1];

    const propDefinition =
      dataDefinition[fullKey] ??
      dataDefinition[isMap]?.value ??
      // Temporary fallback for old maps
      dataDefinition[arbitraryKeys];

    let value = data[key];
    if (state.config?.isPostValidation && alias) {
      key = alias;

      if (value !== undefined) {
        data[key] = value;
      }
    }

    // If the key is not defined in the model and this is a mapped type, validate it
    if (
      dataDefinition[key] === undefined &&
      !aliasedKeys.has(key) &&
      keyDefinition !== Any &&
      keyDefinition !== Unknown
    ) {
      // Remove the old key from the data
      delete data[key];

      key = validate(keyDefinition, key, {
        key: `${state.key}.${key}`,
        path: state.path,

        failOnError: state.failOnError,

        [rootModel]: state[rootModel],
        [currentData]: state[currentData],

        silent: state.silent,
        warnings: state.warnings,

        config: state.config,
      });

      // Insert the data into the object with the new key
      data[key] = value;
    }

    // If the key is not in the definition, this causes no problems downstream, skip it.
    if (!propDefinition) {
      if (state.config?.isPostValidation) {
        delete data[key];
      }

      continue;
    }

    if (state.config?.removeEmpty && isEmpty(value)) {
      delete data[key];
      continue;
    }

    // Skip all validation on `Any` or `Unknown` types
    if (propDefinition === Any || propDefinition === Unknown) {
      continue;
    }

    // If the object is marked as partial or the prop is optional, and the key is not defined, skip it.
    if (isObjectPartial || propDefinition[isOptional]) {
      if (
        value === undefined ||
        // Generally if a value is `null`, it's considered to not exist, but the model may have explicitly
        // defined `null` as a valid value in an optional property
        (value === null &&
          (!isType<IsUnion>(isUnion, propDefinition) ||
            propDefinition.every((ty) => ty !== null)))
      ) {
        // There are logical differences between an `undefined` value being set to `undefined` and the
        // prop not existing at all. If it's optional, setting it to `undefined` is not valid. Delete the key.
        delete data[key];

        continue;
      }
    }

    value = validate(propDefinition, value, {
      key: String(key),
      path: [...state.path, key],

      failOnError: state.failOnError,

      // Objects can be marked as Silent to make all of their properties Silent
      [isSilent]: state[isSilent],

      [isDeepPartial]: state[isDeepPartial],

      [rootModel]: state[rootModel],
      [currentData]: state[currentData],

      silent: state.silent,
      warnings: state.warnings,

      config: state.config,
    });

    if (value === undefined) {
      delete data[key];

      // If we expected the key to exist, return undefined if we are failing on errors
      if (state.failOnError) {
        return undefined;
      }

      // No need to log -- already done in the `validate` call above
    } else {
      data[key] = value;
    }
  }

  data[isValidated] = true;

  return data as M;
}

function resolveSymbol(value: unknown) {
  if (typeof value === "string") {
    const foundSymbol =
      symbolMap[value] ?? symbolMap[`${symbolPrefix}${value}`];

    if (foundSymbol) {
      return foundSymbol;
    }
  }

  return null;
}

function matchType(
  value: unknown,
  type:
    | StringConstructor
    | NumberConstructor
    | BooleanConstructor
    | Uint8ArrayConstructor
    | Uint16ArrayConstructor
    | TypeFunction,
  state: ValidationState,
) {
  // Use the original function if it exists
  type = type[originalFunc] ?? type;

  if (type === String) {
    if (typeof value === "string") {
      return value;
    }
  } else if (type === Number) {
    if (typeof value === "number") {
      return value;
    }

    const result = Number(value);

    if (!isNaN(result)) {
      return result;
    }
  } else if (type === BigInt) {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "bigint" ||
      typeof value === "boolean"
    ) {
      return BigInt(value);
    }

    // BigInt will throw an error if we try and pass in any value other than
    // one of the types above, including `undefined`
    return BigInt(0);
  } else if (type === Boolean) {
    if (typeof value === "boolean") {
      return value;
    }
  } else if (type === Date) {
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === "number" || typeof value === "string") {
      return new Date(value);
    }
  } else if (type === Symbol) {
    const foundSymbol = resolveSymbol(value);

    if (foundSymbol) {
      return foundSymbol;
    }

    // If we didn't find a symbol, fall back to the value unless the value is silent
    if (typeof value !== "symbol") {
      if (state[isSilent] || state.failOnError) {
        return undefined;
      }
    }

    state.mismatchCount++;
    mismatchedTypeWarning(state, type, value);

    return value;
  } else if (isArrayBuffer(type)) {
    if (type === Uint8Array && value instanceof Uint8Array) {
      return value;
    } else if (type === Uint16Array && value instanceof Uint16Array) {
      return value;
    }

    if (Array.isArray(value)) {
      return type.from(value);
    }

    return new type();
  } else if (typeof type === "function") {
    const result = type(value);

    if (result !== undefined) {
      return result;
    }
  } else {
    logValidationWarning(
      state,
      `Failed to specify a valid type at ${state.key}.`,
      { type },
    );

    throw new TypeError(`Failed to specify a valid type at ${state.key}.`);
  }

  if (state.failOnError) {
    return undefined;
  }

  state.mismatchCount++;
  if (!state[isSilent]) {
    mismatchedTypeWarning(state, type, value);
  }

  if (value !== undefined && value !== null) {
    return type(value);
  }

  return type();
}

function isArrayBuffer(
  value: unknown,
): value is Uint8ArrayConstructor | Uint16ArrayConstructor {
  return value === Uint8Array || value === Uint16Array;
}

/**
 * Finds an `[isRootModel]` from a model definition. It will recurse down
 * the object until it finds one.
 *
 * If an object contains two keys with `[isRootModel]` it will log an error
 * and return the `baseModel`.
 */
function findRootModel(model: ModelDefinition): ModelDefinition {
  if (!model || typeof model !== "object") return null;

  const queue: ModelValue[] = [model];

  while (queue.length) {
    const current = queue.shift();

    if (
      current === undefined ||
      current === null ||
      typeof current !== "object"
    ) {
      continue;
    }

    if (current[isRootModel]) {
      return current;
    }

    // If the model is an array, grab its first element and add it to the
    // beginning of the queue. This is to properly handle nested arrays of arrays.
    if (Array.isArray(current)) {
      if (current.length === 0) continue;

      // Ignore non-objects
      if (typeof current[0] === "object") {
        queue.unshift(current[0]);
      }

      continue;
    }

    const foundRootModels: string[] = [];

    for (const key in current) {
      if (current[key]?.[isRootModel]) {
        foundRootModels.push(key);
      }
    }

    // If we found multiple roots, this should be considered a bug as we can't
    // be certain which one to use.
    if (foundRootModels.length > 1) {
      devWarn(
        `Ambiguous root model, [isRootModel] must be set one object higher, or remove the [isRootModel] from one of the direct children: ${foundRootModels.join(
          ", ",
        )}`,
        { model, current },
      );

      break;
    }

    // If we found a root, return it
    if (foundRootModels.length === 1) {
      return current[foundRootModels[0]];
    }

    // Add the object's children to the queue
    queue.push(...Object.values(current));
  }

  return model;
}

const NUMERIC_REGEXP = /^[0-9]+$/;

function isNestedObject(
  type: ModelDefinition | Primitive,
): type is ModelDefinition {
  return (
    typeof type === "object" &&
    !type[isUnion] &&
    type !== Any &&
    type !== Unknown
  );
}

const nestedProxyMap = new WeakMap<object, Record<string, object>>();

function isUnknownOrAny(model: ModelValue): model is Unknown | Any {
  return model === Unknown || model === Any;
}

const ignoredKeys = new Set(["errorCode", "length"]);
export const getWithModel = (model: ModelDefinition) => {
  return (target, key) => {
    let descriptors = nestedProxyMap.get(target);

    if (!descriptors) {
      descriptors = {};

      nestedProxyMap.set(target, descriptors);
    }

    if (typeof key === "symbol") {
      return target[key];
    }

    // We don't really know which one of the models in the Union is the correct one, just ignore them
    if (isType<IsUnion>(isUnion, model)) {
      return target[key];
    }

    if (Array.isArray(model) && NUMERIC_REGEXP.test(key)) {
      const valueType = model[isType<IsTuple>(isTuple, model) ? key : 0];

      // delimeats: I disagree that this should be a warning haha

      // const hasUnknownOrAny =
      //   isUnknownOrAny(model) || isUnknownOrAny(valueType);
      // if (
      //   !IS_NODE &&
      //   mainRefs.enableAccessWarnings &&
      //   hasUnknownOrAny &&
      //   !ignoredKeys.has(key)
      // ) {
      //   devWarn(
      //     `Accessed "${key}" in a data model which is defined as Unknown or Any. Please update the model.`,
      //     { model, target },
      //   );
      // }

      const value = target[key];

      if (isNestedObject(valueType)) {
        if (descriptors[key]) {
          return descriptors[key];
        }

        if (!value) return value;

        const newProxy = new Proxy(value, {
          get: getWithModel(valueType),
        });

        descriptors[key] = newProxy;

        return newProxy;
      }

      return value;
    }

    const modelArbitraryKeys = model[isMap]?.value ?? model[arbitraryKeys];
    const allKeys = new Set(Object.keys(model).map((m) => m.split(":", 2)[0]));

    const valueType = model[key] ?? modelArbitraryKeys;

    if (
      mainRefs.enableAccessWarnings &&
      // `then` is special-cased because the Promise specification allows
      // the `then` property to be read.
      key !== "then" &&
      !NUMERIC_REGEXP.test(key)
    ) {
      if (
        !IS_NODE &&
        (isUnknownOrAny(model) ||
          (isUnknownOrAny(valueType) && !ignoredKeys.has(key)))
      ) {
        devWarn(
          `Accessed "${key}" in a data model which is Unknown or Any. Please update the model.`,
          { model, target },
        );
      }

      if (
        !IS_NODE &&
        mainRefs.enableAccessWarnings &&
        !allKeys.has(key) &&
        !modelArbitraryKeys &&
        !(key in Object.getPrototypeOf(target)) &&
        !key.startsWith("⚡") && // ignore runtime properties
        !model[isSilent] // ignore model marked as silent
      ) {
        devWarn(
          `Data model does not contain field "${key}". Please update the model.`,
          { model, target },
        );
      }
    }

    if (isNestedObject(valueType) && target[key]) {
      if (descriptors[key]) {
        return descriptors[key];
      }

      const newProxy = new Proxy(target[key], { get: getWithModel(valueType) });

      descriptors[key] = newProxy;

      return newProxy;
    }

    return target[key];
  };
};

export function withAccessValidation<V>(data: V, model: ModelDefinition): V {
  if (!model || !data || typeof data !== "object") {
    return data;
  }

  return new Proxy(data, {
    get: getWithModel(model),
  });
}
